import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, ChatCircle, Copy, PaperPlaneTilt, Trash, Spinner, Robot, X, CurrencyDollar, ArrowSquareOut, ArrowsClockwise, ClockClockwise } from "@phosphor-icons/react";

/**
 * Botón inline que va a un lado del campo Características.
 * Funciona en 2 modos:
 *   - Con productId (editar): llama /alendra/products/{id}/sales-pitch
 *   - Sin productId (crear): llama /alendra/sales-pitch-form con form data
 */
export function AlendraInlineButton({ productId, formData, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (loading) return;
    setLoading(true);
    try {
      let data;
      if (productId) {
        ({ data } = await api.post(`/alendra/products/${productId}/sales-pitch`));
      } else {
        if (!formData?.name?.trim()) {
          toast.error("Escribe al menos el nombre del producto antes de generar la descripción.");
          setLoading(false);
          return;
        }
        ({ data } = await api.post(`/alendra/sales-pitch-form`, formData));
      }
      if (data?.text) {
        onGenerated(data.text);
        toast.success("Descripción vendedora generada por Alendra AI ✨");
      } else {
        toast.error("Alendra no devolvió texto");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar la descripción");
    } finally { setLoading(false); }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={loading}
      data-testid="alendra-inline-btn"
      title="Generar descripción vendedora con Alendra AI"
      className="absolute right-2 top-2 w-9 h-9 rounded-md bg-gradient-to-br from-[#002FA7] to-[#003ed4] text-white shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition disabled:opacity-50 disabled:cursor-wait flex items-center justify-center"
    >
      {loading ? (
        <Spinner size={16} className="animate-spin"/>
      ) : (
        <Sparkle size={16} weight="fill"/>
      )}
    </button>
  );
}

/**
 * AlendraAI panel — se inserta dentro del formulario de producto (Crear/Editar).
 * Provee 3 sub-secciones:
 *   1. Generar descripciones (commercial / technical / short_summary)  [opcional, oculta con hideDescription]
 *   2. Generar publicaciones por canal (FB / ML / WA / Web)
 *   3. Chat libre por producto (historial persistido por user+product)
 *
 * Props:
 *   productId: string | null — null si el producto aún no se ha guardado
 *   product:   objeto completo del producto (para mostrar info)
 *   onApplyDescription?: (text) => void — opcional: aplica al campo description del form
 *   hideDescription?: boolean — oculta el tab "Descripción" (cuando ya hay botón inline)
 */
export default function AlendraAI({ productId, product, onApplyDescription, hideDescription }) {
  const tabs = [
    ...(hideDescription ? [] : [{ id: "description", label: "Descripción" }]),
    { id: "publication", label: "Publicación" },
    { id: "seo", label: "SEO" },
    { id: "chat", label: "Chat" },
  ];
  const [tab, setTab] = useState(tabs[0].id);
  const [loading, setLoading] = useState(false);
  const [desc, setDesc] = useState(null);
  const [pubResult, setPubResult] = useState(null);
  const [activeChannel, setActiveChannel] = useState(null);
  const [seoResult, setSeoResult] = useState(null);

  if (!productId) {
    return (
      <div className="border border-zinc-200 rounded-lg p-4 bg-zinc-50 text-sm text-zinc-500 text-center" data-testid="alendra-disabled">
        <Robot size={28} className="inline mb-2 text-zinc-400"/>
        <div><b>Alendra AI</b> se activa una vez que guardes el producto por primera vez.</div>
      </div>
    );
  }

  return (
    <div className="border border-[#002FA7]/20 rounded-lg overflow-hidden bg-gradient-to-br from-[#002FA7]/5 to-transparent" data-testid="alendra-panel">
      <div className="px-4 py-3 border-b border-[#002FA7]/15 bg-[#002FA7]/5 flex items-center gap-2">
        <Sparkle size={18} weight="fill" className="text-[#002FA7]"/>
        <h3 className="text-sm font-bold text-[#002FA7] tracking-tight">Alendra AI</h3>
        <span className="text-[10px] text-zinc-500 ml-auto">Generador de publicaciones y chat por producto</span>
      </div>

      <div className="flex gap-1 px-3 pt-2 text-xs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`alendra-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-t-md font-semibold transition ${tab === t.id ? "bg-white text-[#002FA7] border border-[#002FA7]/20 border-b-white -mb-px" : "text-zinc-500 hover:text-zinc-800"}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="bg-white border-t border-[#002FA7]/15 p-4 min-h-[180px]">
        {tab === "description" && !hideDescription && (
          <DescriptionTab productId={productId} loading={loading} setLoading={setLoading} desc={desc} setDesc={setDesc} onApply={onApplyDescription}/>
        )}
        {tab === "publication" && (
          <PublicationTab productId={productId} loading={loading} setLoading={setLoading} pubResult={pubResult} setPubResult={setPubResult} activeChannel={activeChannel} setActiveChannel={setActiveChannel}/>
        )}
        {tab === "seo" && (
          <SeoTab productId={productId} loading={loading} setLoading={setLoading} seoResult={seoResult} setSeoResult={setSeoResult}/>
        )}
        {tab === "chat" && (
          <ChatTab productId={productId} product={product}/>
        )}
      </div>
    </div>
  );
}

function DescriptionTab({ productId, loading, setLoading, desc, setDesc, onApply }) {
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/alendra/products/${productId}/description`);
      setDesc(data);
      toast.success("Descripción generada por Alendra AI ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar la descripción");
    } finally { setLoading(false); }
  };
  return (
    <div className="space-y-3">
      <button type="button" data-testid="alendra-gen-desc-btn" onClick={run} disabled={loading} className="px-4 py-2 rounded bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002FA7]/90 disabled:opacity-50 inline-flex items-center gap-2">
        {loading ? <Spinner className="animate-spin" size={16}/> : <Sparkle size={16} weight="fill"/>}
        {desc ? "Regenerar descripciones" : "Generar descripciones"}
      </button>
      {desc && (
        <div className="space-y-3" data-testid="alendra-desc-results">
          <DescField label="Descripción Comercial" value={desc.commercial} testId="alendra-desc-commercial" onApply={onApply ? () => { onApply(desc.commercial); toast.success("Aplicada a la descripción del producto"); } : null}/>
          <DescField label="Descripción Técnica" value={desc.technical} testId="alendra-desc-technical"/>
          <DescField label="Resumen Corto (≤ 250 chars)" value={desc.short_summary} testId="alendra-desc-summary" small/>
        </div>
      )}
    </div>
  );
}

function DescField({ label, value, onApply, small, testId }) {
  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback para iframes / permisos bloqueados
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copiado al portapapeles");
    } catch (err) {
      toast.error("Tu navegador bloqueó copiar. Selecciónalo manualmente.");
    }
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wide">{label}</span>
        <div className="ml-auto flex gap-1">
          {onApply && <button type="button" onClick={onApply} className="text-[10px] text-[#002FA7] hover:underline">Aplicar al campo</button>}
          <button type="button" onClick={copy} className="text-[10px] text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1"><Copy size={11}/>Copiar</button>
        </div>
      </div>
      <textarea data-testid={testId} readOnly value={value} rows={small ? 2 : 5} className="w-full text-xs border border-zinc-200 rounded p-2 bg-zinc-50 font-mono leading-relaxed"/>
    </div>
  );
}

const CHANNELS = [
  { id: "facebook_marketplace", label: "Facebook Marketplace", icon: "📘" },
  { id: "mercadolibre", label: "Mercado Libre", icon: "🛒" },
  { id: "whatsapp", label: "WhatsApp", icon: "💬" },
  { id: "website", label: "Sitio Web", icon: "🌐" },
];

function PublicationTab({ productId, loading, setLoading, pubResult, setPubResult, activeChannel, setActiveChannel }) {
  const run = async (channel) => {
    setLoading(true);
    setActiveChannel(channel);
    try {
      const { data } = await api.post(`/alendra/products/${productId}/publication`, { channel });
      setPubResult({ ...data, channel });
      toast.success(`Publicación lista para ${channel}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar la publicación");
    } finally { setLoading(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {CHANNELS.map((c) => (
          <button key={c.id} type="button" data-testid={`alendra-pub-${c.id}`} onClick={() => run(c.id)} disabled={loading} className={`px-3 py-2 rounded border text-xs font-semibold transition inline-flex items-center justify-center gap-1.5 ${activeChannel === c.id ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-zinc-200 hover:border-[#002FA7]/40 hover:bg-[#002FA7]/5"} disabled:opacity-50`}>
            <span>{c.icon}</span>{c.label}
          </button>
        ))}
      </div>
      {loading && <div className="text-xs text-zinc-500 inline-flex items-center gap-2"><Spinner className="animate-spin" size={14}/>Generando publicación…</div>}
      {pubResult && !loading && (
        <div className="space-y-3" data-testid="alendra-pub-results">
          <DescField label={`Título — ${CHANNELS.find((c) => c.id === pubResult.channel)?.label}`} value={pubResult.title} testId="alendra-pub-title" small/>
          <DescField label="Descripción / Mensaje" value={pubResult.description} testId="alendra-pub-description"/>
        </div>
      )}
    </div>
  );
}

function SeoTab({ productId, loading, setLoading, seoResult, setSeoResult }) {
  // Cargar SEO ya guardado al montar
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/alendra/products/${productId}/seo`);
        if (!cancelled && data && (data.meta_title || data.meta_description || data.keywords || data.seo_text)) {
          setSeoResult({ ...data, saved: true });
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [productId]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/alendra/products/${productId}/seo`);
      setSeoResult(data);
      toast.success("SEO generado y guardado en el producto ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar el SEO");
    } finally { setLoading(false); }
  };
  return (
    <div className="space-y-3" data-testid="alendra-seo">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid="alendra-gen-seo-btn"
          onClick={run}
          disabled={loading}
          className="px-4 py-2 rounded bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002FA7]/90 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {loading ? <Spinner className="animate-spin" size={16}/> : <Sparkle size={16} weight="fill"/>}
          {seoResult ? "Regenerar SEO" : "Generar SEO"}
        </button>
        {seoResult?.saved && (
          <span className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            ✓ Guardado en el producto · visible en la tienda
          </span>
        )}
        <span className="text-[10px] text-zinc-500 w-full">Se guarda automáticamente en el producto y la tienda pública lo usa en el &lt;head&gt; para Google.</span>
      </div>
      {seoResult && (
        <div className="space-y-3" data-testid="alendra-seo-results">
          <DescField label="Meta Title (50-60 chars)" value={seoResult.meta_title} testId="alendra-seo-title" small/>
          <DescField label="Meta Description (140-160 chars)" value={seoResult.meta_description} testId="alendra-seo-description" small/>
          <DescField label="Keywords (CSV)" value={seoResult.keywords} testId="alendra-seo-keywords" small/>
          <DescField label="Texto SEO (HERO / HEAD)" value={seoResult.seo_text} testId="alendra-seo-text"/>
        </div>
      )}
    </div>
  );
}

/**
 * AlendraSeoModal — modal independiente que muestra el SEO de un producto.
 * Útil para mostrar el botón rápido en la lista de productos.
 */
export function AlendraSeoModal({ productId, productName, onClose }) {
  const [loading, setLoading] = useState(false);
  const [seoResult, setSeoResult] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Cargar SEO existente al abrir; solo generar si no hay nada guardado.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/alendra/products/${productId}/seo`);
        if (cancelled) return;
        if (data && (data.meta_title || data.meta_description || data.keywords || data.seo_text)) {
          setSeoResult({ ...data, saved: true });
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setInitialLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/alendra/products/${productId}/seo`);
      setSeoResult(data);
      toast.success("SEO generado y guardado en el producto ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar el SEO");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="alendra-seo-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2 sticky top-0 bg-white">
          <Sparkle size={18} weight="fill" className="text-[#002FA7]"/>
          <h3 className="font-bold text-sm">SEO · {productName || "Producto"}</h3>
          {seoResult?.saved && (
            <span className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              ✓ Guardado · visible en la tienda
            </span>
          )}
          <button type="button" onClick={onClose} className="ml-auto p-1 hover:bg-zinc-100 rounded" data-testid="alendra-seo-modal-close">
            <X size={16}/>
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              data-testid="alendra-seo-modal-regen"
              onClick={run}
              disabled={loading || initialLoading}
              className="px-4 py-2 rounded bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002FA7]/90 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {loading ? <Spinner className="animate-spin" size={16}/> : <Sparkle size={16} weight="fill"/>}
              {seoResult ? "Regenerar SEO" : "Generar SEO"}
            </button>
            <span className="text-[10px] text-zinc-500">Se guarda en el producto y aparece en la tienda para Google.</span>
          </div>
          {initialLoading && (
            <div className="text-xs text-zinc-500 inline-flex items-center gap-2"><Spinner className="animate-spin" size={14}/>Cargando SEO actual…</div>
          )}
          {loading && (
            <div className="text-xs text-zinc-500 inline-flex items-center gap-2"><Spinner className="animate-spin" size={14}/>Generando SEO con Alendra AI…</div>
          )}
          {seoResult && (
            <div className="space-y-3" data-testid="alendra-seo-modal-results">
              <DescField label="Meta Title (50-60 chars)" value={seoResult.meta_title} testId="alendra-seo-modal-title" small/>
              <DescField label="Meta Description (140-160 chars)" value={seoResult.meta_description} testId="alendra-seo-modal-description" small/>
              <DescField label="Keywords (CSV)" value={seoResult.keywords} testId="alendra-seo-modal-keywords" small/>
              <DescField label="Texto SEO (HERO / HEAD)" value={seoResult.seo_text} testId="alendra-seo-modal-text"/>
            </div>
          )}
          {!initialLoading && !loading && !seoResult && (
            <div className="border border-dashed border-zinc-200 rounded-lg p-6 text-center bg-zinc-50">
              <Sparkle size={32} weight="fill" className="inline mb-2 text-zinc-300"/>
              <p className="text-sm text-zinc-600">Este producto aún no tiene SEO. Dale click a <b>Generar SEO</b> para crearlo y guardarlo automáticamente.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function ChatTab({ productId, product }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/alendra/products/${productId}/chat`);
      setMessages(data.messages || []);
    } catch (err) {
      console.error(err);
    } finally { setLoaded(true); }
  };
  useEffect(() => { loadHistory(); }, [productId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    setSending(true);
    try {
      const { data } = await api.post(`/alendra/products/${productId}/chat`, { message: text });
      setMessages((prev) => [...prev.filter((m) => m.id !== optimistic.id), ...data.messages]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Alendra AI no respondió");
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setInput(text);
    } finally { setSending(false); }
  };

  const clear = async () => {
    if (!confirm("¿Borrar todo el historial de chat de este producto?")) return;
    try {
      await api.delete(`/alendra/products/${productId}/chat`);
      setMessages([]);
      toast.success("Historial borrado");
    } catch (err) {
      toast.error("No se pudo borrar el historial");
    }
  };

  const quickPrompts = [
    "Genera 5 títulos para Facebook Marketplace.",
    "Genera una descripción más agresiva.",
    "¿Qué beneficios tiene para estudiantes?",
    "Genera texto para WhatsApp.",
  ];

  return (
    <div className="space-y-2" data-testid="alendra-chat">
      <div className="border border-zinc-200 rounded h-[260px] overflow-y-auto p-2 bg-zinc-50 space-y-2 text-xs">
        {!loaded && <div className="text-zinc-400">Cargando…</div>}
        {loaded && messages.length === 0 && (
          <div className="text-zinc-400 text-center pt-4">
            <Robot size={28} className="inline mb-1 text-zinc-300"/>
            <div>Hazle una pregunta a Alendra AI sobre <b>{product?.name || "este producto"}</b></div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-lg whitespace-pre-wrap leading-snug ${m.role === "user" ? "bg-[#002FA7] text-white" : "bg-white border border-zinc-200 text-zinc-800"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && <div className="text-zinc-400 inline-flex items-center gap-1.5"><Spinner className="animate-spin" size={12}/>Alendra está escribiendo…</div>}
        <div ref={endRef}/>
      </div>

      {messages.length === 0 && (
        <div className="flex flex-wrap gap-1.5">
          {quickPrompts.map((p) => (
            <button key={p} type="button" onClick={() => setInput(p)} className="text-[10px] px-2 py-1 rounded-full border border-zinc-200 hover:border-[#002FA7]/40 hover:bg-[#002FA7]/5 text-zinc-700">{p}</button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input data-testid="alendra-chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Escribe tu pregunta…" className="flex-1 text-xs border border-zinc-200 rounded px-2 py-1.5"/>
        <button type="button" data-testid="alendra-chat-send" onClick={send} disabled={sending || !input.trim()} className="px-3 py-1.5 rounded bg-[#002FA7] text-white text-xs font-semibold hover:bg-[#002FA7]/90 disabled:opacity-50 inline-flex items-center gap-1.5">
          <PaperPlaneTilt size={12}/>Enviar
        </button>
        {messages.length > 0 && (
          <button type="button" data-testid="alendra-chat-clear" onClick={clear} className="px-2 py-1.5 rounded border border-zinc-200 hover:border-red-300 hover:bg-red-50 text-red-600" title="Borrar historial"><Trash size={12}/></button>
        )}
      </div>
    </div>
  );
}


/**
 * AlendraPricingModal — modal de Pricing AI con historial de análisis.
 * Usa OpenAI Web Search (Responses API) para buscar el producto en Mercado Libre,
 * Amazon MX, eBay y Google Shopping en tiempo real, y devuelve min/recomendado/
 * premium con fuentes citadas. Cada análisis se guarda y aparece en el historial.
 */
const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const MX_CITIES = [
  { value: "", label: "🇲🇽 Todo México" },
  { value: "Tijuana", label: "Tijuana" },
  { value: "CDMX", label: "Ciudad de México" },
  { value: "Monterrey", label: "Monterrey" },
  { value: "Guadalajara", label: "Guadalajara" },
  { value: "Puebla", label: "Puebla" },
  { value: "Querétaro", label: "Querétaro" },
  { value: "Mérida", label: "Mérida" },
  { value: "Mexicali", label: "Mexicali" },
  { value: "Cancún", label: "Cancún" },
  { value: "León", label: "León" },
  { value: "Ciudad Juárez", label: "Ciudad Juárez" },
];

export function AlendraPricingModal({ productId, productName, currentStorePrice, onClose }) {
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [mode, setMode] = useState("search"); // 'search' | 'url' | 'chat'
  const [urlInput, setUrlInput] = useState("");
  // Chat libre state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const chatScrollRef = useRef(null);
  // Default ciudad: la última que usó el usuario (localStorage) o Tijuana.
  const [city, setCity] = useState(() => {
    try { return localStorage.getItem("alendra_pricing_city") ?? "Tijuana"; }
    catch { return "Tijuana"; }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/alendra/products/${productId}/pricing/history`);
        if (cancelled) return;
        setHistory(data.items || []);
        if ((data.items || []).length > 0) setActive(data.items[0]);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "No se pudo cargar el historial");
      } finally { if (!cancelled) setLoadingHistory(false); }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  // Cargar chat al cambiar a modo chat por primera vez
  useEffect(() => {
    if (mode !== "chat" || chatMessages.length > 0 || chatLoading) return;
    setChatLoading(true);
    api.get(`/alendra/products/${productId}/pricing/chat`)
      .then((r) => setChatMessages(r.data.messages || []))
      .catch(() => {})
      .finally(() => setChatLoading(false));
  // eslint-disable-next-line
  }, [mode, productId]);

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages, chatSending]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    setChatMessages((prev) => [...prev, { role: "user", content: text, at: new Date().toISOString() }]);
    setChatInput("");
    try {
      const { data } = await api.post(`/alendra/products/${productId}/pricing/chat`, { message: text });
      setChatMessages(data.messages || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en el chat. Intenta de nuevo.");
      setChatMessages((prev) => prev.slice(0, -1));
    } finally { setChatSending(false); }
  };

  const clearChat = async () => {
    if (!confirm("¿Borrar todo el historial del chat de pricing para este producto?")) return;
    try {
      await api.delete(`/alendra/products/${productId}/pricing/chat`);
      setChatMessages([]);
      toast.success("Chat reiniciado");
    } catch { toast.error("No se pudo borrar"); }
  };

  const runNew = async () => {
    if (mode === "chat") { sendChat(); return; }
    if (mode === "url") {
      const u = urlInput.trim();
      if (!u || !/^https?:\/\//i.test(u)) {
        toast.error("Pega una URL completa (debe empezar con http:// o https://)");
        return;
      }
    }
    setGenerating(true);
    try {
      try { localStorage.setItem("alendra_pricing_city", city); } catch { /* ignore */ }
      const payload = mode === "url"
        ? { url: urlInput.trim() }
        : { city: city || null };
      const { data } = await api.post(`/alendra/products/${productId}/pricing`, payload);
      setHistory((prev) => [data, ...prev]);
      setActive(data);
      toast.success(mode === "url" ? "Listado analizado" : `Análisis generado · ${city || "todo México"}`);
      if (mode === "url") setUrlInput("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo analizar el precio. Intenta de nuevo.");
    } finally { setGenerating(false); }
  };

  const remove = async (aid) => {
    if (!confirm("¿Eliminar este análisis del historial?")) return;
    try {
      await api.delete(`/alendra/pricing/${aid}`);
      const remaining = history.filter((h) => h.id !== aid);
      setHistory(remaining);
      if (active?.id === aid) setActive(remaining[0] || null);
      toast.success("Análisis eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="alendra-pricing-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-zinc-200 bg-white">
          <div className="px-5 py-3 flex items-center gap-2 flex-wrap">
            <CurrencyDollar size={20} weight="fill" className="text-emerald-600"/>
            <h3 className="font-bold text-sm">Pricing AI · {productName || "Producto"}</h3>
            <span className="text-[10px] text-zinc-500 hidden lg:inline">— marketplaces MX vía OpenAI Web Search</span>
            <button type="button" onClick={onClose} className="ml-auto p-1 hover:bg-zinc-100 rounded" data-testid="pricing-close">
              <X size={16}/>
            </button>
          </div>
          {/* Mode tabs + controls */}
          <div className="px-5 pb-3 flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-0.5 text-xs">
              <button
                type="button"
                onClick={()=>setMode("search")}
                data-testid="pricing-mode-search"
                className={`px-3 py-1.5 rounded font-semibold transition ${mode === "search" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
              >
                🔍 Búsqueda
              </button>
              <button
                type="button"
                onClick={()=>setMode("url")}
                data-testid="pricing-mode-url"
                className={`px-3 py-1.5 rounded font-semibold transition ${mode === "url" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
              >
                🔗 Link
              </button>
              <button
                type="button"
                onClick={()=>setMode("chat")}
                data-testid="pricing-mode-chat"
                className={`px-3 py-1.5 rounded font-semibold transition ${mode === "chat" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
              >
                🤖 Chat libre con web
              </button>
            </div>
            {mode === "search" && (
              <select
                value={city}
                onChange={(e)=>setCity(e.target.value)}
                data-testid="pricing-city-selector"
                className="text-xs border border-zinc-300 rounded px-2 py-1.5 bg-white hover:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                title="Ciudad a priorizar en la búsqueda"
              >
                {MX_CITIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            )}
            {mode === "url" && (
              <input
                type="url"
                value={urlInput}
                onChange={(e)=>setUrlInput(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === "Enter" && !generating) { e.preventDefault(); runNew(); } }}
                placeholder="Pega URL de FB Marketplace, ML, OLX, Segundamano…"
                data-testid="pricing-url-input"
                className="flex-1 min-w-[200px] text-xs border border-zinc-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            )}
            {mode === "chat" && chatMessages.length > 0 && (
              <button
                type="button"
                onClick={clearChat}
                data-testid="pricing-chat-clear"
                className="text-[10px] text-zinc-500 hover:text-rose-600 underline"
              >
                Borrar historial
              </button>
            )}
            {mode !== "chat" && (
              <button
                type="button"
                onClick={runNew}
                disabled={generating || (mode === "url" && !urlInput.trim())}
                data-testid="pricing-run"
                className="px-3 py-1.5 rounded bg-[#002FA7] hover:bg-[#002277] text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {generating ? <Spinner className="animate-spin" size={14}/> : <ArrowsClockwise size={14}/>}
                {mode === "url" ? "Analizar link" : (history.length > 0 ? "Nuevo análisis" : "Analizar")}
              </button>
            )}
          </div>
        </div>

        {mode === "chat" ? (
          <div className="flex-1 min-h-0 flex flex-col bg-zinc-50">
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="pricing-chat-messages">
              {chatLoading && (
                <div className="text-center text-xs text-zinc-400"><Spinner size={16} className="animate-spin inline mr-2"/>Cargando chat…</div>
              )}
              {!chatLoading && chatMessages.length === 0 && (
                <div className="text-center text-xs text-zinc-500 py-8 max-w-md mx-auto" data-testid="pricing-chat-empty">
                  <ChatCircle size={36} className="inline mb-2 text-zinc-300"/>
                  <p className="font-semibold text-sm mb-2">Pregúntale a Alendra con tu propio prompt</p>
                  <p>La AI tiene acceso a navegación web en tiempo real. Sé específico:</p>
                  <ul className="text-left mt-3 space-y-1.5 italic text-zinc-500 bg-white border border-zinc-200 rounded p-3">
                    <li>&bull; &ldquo;Búscame en FB Marketplace Tijuana los 5 listados más baratos&rdquo;</li>
                    <li>&bull; &ldquo;Compara con eBay USA y dime cuánto sale importarlo&rdquo;</li>
                    <li>&bull; &ldquo;¿Qué accesorios suelen incluir los vendedores en ML?&rdquo;</li>
                    <li>&bull; &ldquo;¿Cuánto se ha movido el precio en los últimos 30 días?&rdquo;</li>
                  </ul>
                </div>
              )}
              {chatMessages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`pricing-chat-message-${i}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "bg-emerald-600 text-white" : "bg-white border border-zinc-200 text-zinc-800"}`} data-testid={`pricing-chat-message-${m.role}-${i}`}>
                    <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                    <div className={`text-[9px] mt-1 ${m.role === "user" ? "text-white/60" : "text-zinc-400"}`}>
                      {m.at ? new Date(m.at).toLocaleTimeString("es-MX", { hour:"2-digit", minute:"2-digit" }) : ""}
                    </div>
                  </div>
                </div>
              ))}
              {chatSending && (
                <div className="flex justify-start" data-testid="pricing-chat-typing">
                  <div className="bg-white border border-zinc-200 rounded-lg px-3 py-2 text-xs text-zinc-500">
                    <Spinner size={12} className="animate-spin inline mr-1"/>Alendra buscando en internet…
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-zinc-200 p-3 bg-white">
              <div className="flex gap-2">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                  placeholder="Escribe tu prompt (Alendra buscará en internet en vivo)…"
                  rows={2}
                  data-testid="pricing-chat-input"
                  className="flex-1 border border-zinc-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
                />
                <button
                  type="button"
                  onClick={sendChat}
                  disabled={!chatInput.trim() || chatSending}
                  data-testid="pricing-chat-send"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 disabled:opacity-50"
                  title="Enviar (Enter)"
                >
                  <PaperPlaneTilt size={18} weight="fill"/>
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] flex-1 min-h-0">
          {/* Historial */}
          <aside className="border-r border-zinc-200 bg-zinc-50 p-2 overflow-y-auto" data-testid="pricing-history">
            <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
              <ClockClockwise size={12}/> Historial
              <span className="ml-auto bg-white px-1.5 rounded text-[9px] border border-zinc-200">{history.length}</span>
            </div>
            {loadingHistory && <div className="text-xs text-zinc-400 p-3 text-center"><Spinner size={14} className="animate-spin inline mr-1"/>Cargando…</div>}
            {!loadingHistory && history.length === 0 && (
              <div className="text-xs text-zinc-400 p-3 text-center">
                <Robot size={28} className="inline mb-1 text-zinc-300"/>
                <div>Sin análisis. Da click en <b>Analizar</b>.</div>
              </div>
            )}
            <ul className="space-y-1">
              {history.map((h) => {
                const isActive = active?.id === h.id;
                return (
                  <li key={h.id} className="relative group">
                    <button
                      type="button"
                      onClick={() => setActive(h)}
                      data-testid={`pricing-history-item-${h.id}`}
                      className={`w-full text-left px-2 py-1.5 pr-7 rounded text-xs transition ${isActive ? "bg-emerald-600 text-white" : "hover:bg-white text-zinc-800 border border-transparent hover:border-zinc-200"}`}
                    >
                      <div className="font-semibold truncate">
                        {new Date(h.generated_at).toLocaleDateString("es-MX", { day:"2-digit", month:"short" })} {new Date(h.generated_at).toLocaleTimeString("es-MX", { hour:"2-digit", minute:"2-digit" })}
                      </div>
                      <div className={`text-[10px] ${isActive ? "text-white/80" : "text-zinc-500"}`}>
                        Rec: {fmtMXN(h.recommended_price_mxn)} · {h.sources?.length || 0} fuentes
                      </div>
                      <div className={`text-[9px] mt-0.5 flex items-center gap-1 flex-wrap ${isActive ? "text-white/70" : "text-zinc-400"}`}>
                        {h.analyzed_url && <span>🔗 link</span>}
                        {h.is_used_search && <span>🔁 usado</span>}
                        {h.city && !h.analyzed_url && <span>· 📍 {h.city}</span>}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); remove(h.id); }}
                      data-testid={`pricing-delete-${h.id}`}
                      className={`absolute right-1 top-1.5 opacity-0 group-hover:opacity-100 transition p-1 rounded ${isActive ? "hover:bg-white/20 text-white" : "hover:bg-red-100 text-red-600"}`}
                      title="Eliminar"
                    >
                      <Trash size={10}/>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Detalle */}
          <section className="overflow-y-auto p-5">
            {!active && !generating && (
              <div className="border border-dashed border-zinc-200 rounded-lg p-10 text-center bg-zinc-50 h-full flex flex-col items-center justify-center">
                <CurrencyDollar size={48} weight="fill" className="text-zinc-300 mb-2"/>
                <p className="text-sm text-zinc-500 max-w-md">
                  Da click en <b>Analizar</b> para que Alendra busque en Mercado Libre, Amazon, eBay y Google Shopping cuánto se vende este producto y te dé un rango sugerido.
                </p>
              </div>
            )}
            {generating && (
              <div className="border border-zinc-200 rounded-lg p-10 text-center h-full flex flex-col items-center justify-center">
                <Spinner size={32} className="animate-spin text-emerald-600 mb-2"/>
                <p className="text-sm text-zinc-600">Buscando en internet…</p>
                <p className="text-xs text-zinc-400 mt-1">Esto puede tardar 15-40 segundos</p>
              </div>
            )}
            {active && !generating && (
              <PricingDetail data={active} currentStorePrice={currentStorePrice}/>
            )}
          </section>
        </div>
        )}
      </div>
    </div>
  );
}

function PricingDetail({ data, currentStorePrice }) {
  const confidence = data.confidence || "medium";
  const confidenceColor = confidence === "high" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : confidence === "low" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-zinc-100 text-zinc-700 border-zinc-200";
  const cur = Number(currentStorePrice || 0);
  const rec = Number(data.recommended_price_mxn || 0);
  const diff = cur && rec ? cur - rec : 0;
  const diffPct = cur && rec ? ((cur - rec) / rec) * 100 : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${confidenceColor}`}>
          Confianza: {confidence}
        </span>
        {data.analyzed_url && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200" data-testid="pricing-url-badge" title={data.analyzed_url}>
            🔗 Link único
          </span>
        )}
        {data.is_used_search && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200" data-testid="pricing-used-badge">
            🔁 Mercado de usado
          </span>
        )}
        {data.city && !data.analyzed_url && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200" data-testid="pricing-city-badge">
            📍 {data.city}
          </span>
        )}
        <span className="text-[10px] text-zinc-400">Generado: {new Date(data.generated_at).toLocaleString("es-MX")}</span>
        <span className="text-[10px] text-zinc-400 ml-auto">Modelo: {data.model}</span>
      </div>

      {/* Price tiers */}
      <div className="grid grid-cols-3 gap-3">
        <PriceTier
          label="Mínimo"
          value={data.min_price_mxn}
          color="bg-rose-50 border-rose-200 text-rose-700"
          help="Precio piso del mercado — vende rápido pero margen bajo"
          testId="pricing-min"
        />
        <PriceTier
          label="Recomendado"
          value={data.recommended_price_mxn}
          color="bg-emerald-50 border-emerald-300 text-emerald-700"
          help="Competitivo + margen saludable"
          featured
          testId="pricing-recommended"
        />
        <PriceTier
          label="Premium"
          value={data.premium_price_mxn}
          color="bg-violet-50 border-violet-200 text-violet-700"
          help="Justificable si la condición/marca lo soportan"
          testId="pricing-premium"
        />
      </div>

      {/* Comparación con precio actual */}
      {cur > 0 && (
        <div data-testid="pricing-current-comparison" className={`p-3 rounded-lg border text-sm ${
          Math.abs(diffPct) < 5 ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
          diff > 0 ? "bg-amber-50 border-amber-200 text-amber-800" :
          "bg-rose-50 border-rose-200 text-rose-800"
        }`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <span className="text-xs uppercase tracking-wider font-semibold">Tu precio actual de tienda</span>
              <div className="font-bold text-lg">{fmtMXN(cur)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider">vs Recomendado</div>
              <div className="font-bold text-lg">
                {diff > 0 ? "+" : ""}{fmtMXN(diff)} ({diffPct.toFixed(1)}%)
              </div>
            </div>
          </div>
          <div className="text-xs mt-1 opacity-80">
            {Math.abs(diffPct) < 5 ? "✓ Estás alineado con el mercado." :
              diff > 0 ? "⚠️ Estás por encima del mercado — considera bajar para vender más rápido." :
              "💡 Estás por debajo del mercado — podrías subir el precio sin perder ventas."}
          </div>
        </div>
      )}

      {/* Summary */}
      {data.summary && (
        <div className="border border-zinc-200 rounded-lg p-3 bg-white text-sm" data-testid="pricing-summary">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Análisis</div>
          <p className="text-zinc-700 leading-relaxed">{data.summary}</p>
        </div>
      )}

      {/* Sources */}
      {(data.sources || []).length > 0 && (
        <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="pricing-sources">
          <div className="px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-600 font-semibold border-b border-zinc-200">
            Fuentes citadas ({data.sources.length})
          </div>
          <ul className="divide-y divide-zinc-100">
            {data.sources.map((s, i) => (
              <li key={i} className="px-3 py-2 hover:bg-zinc-50 text-xs flex items-center gap-3">
                <span className="font-mono text-[10px] text-zinc-400 w-4 shrink-0">#{i+1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-800 truncate" title={s.title}>{s.title}</div>
                  <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 flex-wrap">
                    <span>{s.site}</span>
                    {s.condition && (
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${
                        s.condition === "usado" || s.condition === "seminuevo" ? "bg-amber-100 text-amber-700" :
                        s.condition === "reacondicionado" ? "bg-blue-100 text-blue-700" :
                        s.condition === "nuevo" ? "bg-emerald-100 text-emerald-700" :
                        "bg-zinc-100 text-zinc-600"
                      }`}>{s.condition}</span>
                    )}
                    {s.city && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-blue-50 text-blue-700">📍 {s.city}</span>
                    )}
                  </div>
                </div>
                <div className="font-bold text-emerald-700 numeric shrink-0">{fmtMXN(s.price_mxn)}</div>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-zinc-200 rounded shrink-0" title="Abrir">
                    <ArrowSquareOut size={12}/>
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PriceTier({ label, value, color, help, featured, testId }) {
  return (
    <div className={`relative p-3 rounded-lg border ${color} ${featured ? "ring-2 ring-emerald-400 scale-[1.02]" : ""}`} data-testid={testId}>
      {featured && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">★ Mejor</span>}
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-extrabold tracking-tight my-1 numeric">{fmtMXN(value)}</div>
      <div className="text-[10px] opacity-70 leading-tight">{help}</div>
    </div>
  );
}
